import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ParcoursHeader } from "./components/ParcoursHeader";
import { ParcoursStepList } from "./components/ParcoursStepList";
import { allParcours } from "../../modules/academy/parcours/demo/demoParcours";
import { useParcours } from "../../context/ParcoursContext";
import type { ParcoursStep } from "../../modules/academy/parcours/models/ParcoursStep";

export default function ParcoursPage() {
  const { parcoursId } = useParams<{ parcoursId: string }>();
  const navigate = useNavigate();
  const { parcoursProgress, isGuest, startParcours, markStepCompleted, getCurrentStep, getNextStep } =
    useParcours();

  const parcours = useMemo(() => allParcours.find((p) => p.id === parcoursId), [parcoursId]);

  if (!parcours) {
    return (
      <main className="max-w-3xl mx-auto py-10 space-y-3">
        <h1 className="text-2xl font-bold">Parcours introuvable</h1>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Le parcours demandé n'existe pas. Retournez vers Academy.
        </p>
        <button
          type="button"
          onClick={() => navigate("/academy")}
          className="text-blue-600 underline text-sm"
        >
          Revenir à Academy
        </button>
      </main>
    );
  }

  const safeParcours = parcours;
  const state = parcoursProgress[safeParcours.id];
  const completedSteps = state?.completedSteps || [];
  const currentStep = getCurrentStep(safeParcours.id, safeParcours.steps);
  const nextStep = getNextStep(safeParcours.id, safeParcours.steps);
  const progressionPercent = safeParcours.steps.length
    ? Math.round((completedSteps.length / safeParcours.steps.length) * 100)
    : 0;
  const firstStepId = safeParcours.steps[0]?.id;

  function handleStart() {
    startParcours(safeParcours.id, firstStepId);
  }

  function handleContinue() {
    if (currentStep) {
      handleOpenStep(currentStep);
    }
  }

  function handleOpenStep(step: ParcoursStep) {
    // V1 : on se contente de routage de haut niveau ou log TODO
    if (step.type === "micro" || step.type === "module" || step.type === "quiz") {
      // On suppose que les étapes sont liées au thème biais-confirmation pour la démo
      navigate("/theme/biais-confirmation");
      return;
    }
    if (step.type === "play") {
      navigate(`/parcours/${safeParcours.id}/play/${step.id}`);
      return;
    }
    if (step.type === "assist") {
      navigate(`/parcours/${safeParcours.id}/assist/${step.id}`);
      return;
    }
    // TODO MixMind: brancher play/assist/resource spécifiques
    console.log("TODO open step", step);
  }

  function handleMarkDone(step: ParcoursStep) {
    markStepCompleted(safeParcours.id, step.id, safeParcours.steps);
  }

  return (
    <main className="max-w-5xl mx-auto py-8 px-4 space-y-6">
      <ParcoursHeader
        parcours={safeParcours}
        progressionPercent={progressionPercent}
        isGuest={isGuest}
        onStart={handleStart}
        onContinue={handleContinue}
      />

      <ParcoursStepList
        parcours={safeParcours}
        completedSteps={completedSteps}
        currentStepId={currentStep?.id}
        isGuest={isGuest}
        onOpenStep={handleOpenStep}
        onMarkDone={handleMarkDone}
      />

      {nextStep && (
        <div className="rounded-2xl border shadow-sm p-4 bg-white dark:bg-slate-900 dark:border-slate-700 space-y-2">
          <p className="text-sm font-semibold">Étape suivante</p>
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {nextStep.title || nextStep.type} ({nextStep.type})
          </p>
          <button
            type="button"
            onClick={() => handleOpenStep(nextStep)}
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            Ouvrir l'étape suivante
          </button>
        </div>
      )}
    </main>
  );
}
